
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ISorter, IFilter } from "@/models/tables";
import { DefaultFilters } from "@/data/filters/imports";
import { DefaultSorters } from "@/data/sorters/imports";

// Types
import {
  IImportsProviderData,
  IImportsProviderMethods
} from "@/models/providers/imports";
import { IImport } from "@/models/imports";

@Component
export default class ImportsProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: `IP1` /** [I]mports  [P]rovider */ })
  public declare id: string;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data

  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public hasRequestError = false;

  public dataModule = DataModules.IMPORTS;

  public filters = this.availableFilters;
  public sorters = this.availableSorters;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.getData();
  }

  // Methods

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public createImport() {
    return this.$store.dispatch(
      `data/${DataModules.IMPORTS}/openCreateImportModal`,
      {
        events: {
          success: this.reloadData
        }
      }
    );
  }

  public async deleteImport(importId: IImport["id"]) {
    try {
      await this.$store.dispatch(`data/${DataModules.IMPORTS}/confirmDelete`, {
        importId
      });

      this.getData({ ignoreStored: true });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public getStoreParams(params: any): Promise<any> {
    const defaultParams = {
      with: ["credentials", "status", "source"].join()
    };
    return _.merge({ params: defaultParams }, params);
  }

  // Computed

  public get canDelete() {
    return this.$userCan("delete_import");
  }

  // Guarding

  public guardFunctionalities = ["list_imports"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("importsProviderData")
  public providerData(): IImportsProviderData {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      canDelete: this.canDelete,
      constQueryFilters: this.constQueryFilters,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasRequestError: this.hasRequestError,
      imports: this.table.data as IImport[],
      isLoading: this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      table: this.table,
      total: this.table.total,
      guardFunctionalities: this.guardFunctionalities
    };
  }

  @Provide("importsProviderMethods")
  public get providerMethods(): IImportsProviderMethods {
    return {
      createImport: this.createImport,
      deleteImport: this.deleteImport,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $importsData: this.providerData(),
        $importsMethods: this.providerMethods
      })
    );
  }
}
